import { useTranslation } from "react-i18next";
import BaseButton from "@/components/base/button/BaseButton.tsx";
import Image from "@/components/base/Image.tsx";
import IconClose from "@/assets/icons/close.svg?react";
import IconClock from "@/assets/icons/clock.svg?react";
import IconDonFilledLinear from "@/assets/icons/don-filled-linear.svg?react";
import IconShoppingCart from "@/assets/icons/shopping-cart.svg?react";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer.tsx";
import { formatNumber } from "@/shared/utils";
import { sanitizeHtml } from "@/shared/utils/sanitizeHtml";
import { openLink } from "@telegram-apps/sdk-react";
import dayjs from "dayjs";
import type { Product } from "../api";

interface ProductDetailSheetProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function ProductDetailSheet({
  product,
  isOpen,
  onClose,
}: ProductDetailSheetProps) {
  const { t } = useTranslation();

  if (!product) return null;
  const p = product;

  const handleOpenLink = () => {
    if (p.link) {
      openLink(p.link, { tryInstantView: false });
    }
  };

  const hasFooter = !!p.link;

  return (
    <Drawer open={isOpen} onOpenChange={(e) => !e && onClose()}>
      <DrawerContent className="bg-gray-4 max-h-[85vh]" showTopThumb>
        <DrawerHeader className="flex justify-between items-center">
          <DrawerTitle className="text-lg">
            {p.title}
          </DrawerTitle>
          <DrawerClose>
            <IconClose className="size-6 text-gray-1 cursor-pointer" />
          </DrawerClose>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto" style={{ overscrollBehavior: "contain" }}>
          {/* Hero image — 16:9, edge-to-edge */}
          <div className="relative w-full aspect-[16/9] bg-white/[0.04] overflow-hidden mb-4">
            {p.icon ? (
              <Image
                urls={p.icon}
                size="medium"
                imgClassName="w-full h-full object-cover"
                wrapperClassName="w-full h-full"
                alt={p.title}
              />
            ) : (
              <div className="w-full h-full flex-center bg-gradient-to-br from-orange-100/15 via-gray-4 to-gray-4">
                <IconShoppingCart className="size-16 text-orange-100/60" />
              </div>
            )}
            {p.is_hot && (
              <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-red text-white text-[10px] font-bold leading-none uppercase tracking-wide">
                HOT
              </span>
            )}
          </div>

          <div className="px-4">
            {p.description && (
              <div className="mb-4">
                <div
                  className="voucher-richtext text-sm text-white/80 leading-150 font-normal"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(p.description) }}
                />
              </div>
            )}

            {/* DON reward tile */}
            <div className="w-full mb-4 rounded-xl bg-white/08 border border-orange-100/30 px-4 py-3 flex items-center gap-2">
              <IconDonFilledLinear width="20" height="20" />
              <span className="text-sm text-gray-1 leading-130">
                {t("reward")}
              </span>
              <span className="ml-auto text-base font-bold text-orange-100 leading-130">
                {formatNumber(p.don_reward)} DON
              </span>
            </div>

            {/* Details */}
            {p.details && (
              <div className="rounded-xl bg-white/08 p-3 mb-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-orange-100/80 leading-130 mb-2">
                  {t("voucher_details") || "Qo'shimcha ma'lumot"}
                </p>
                <div
                  className="voucher-richtext text-sm text-white/90 leading-[170%] font-normal"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(p.details) }}
                />
              </div>
            )}

            {/* Expiry */}
            {p.end_date && (
              <div className="flex items-center gap-1 text-gray-1 mb-4">
                <IconClock className="size-4" />
                <span className="text-xs leading-130">
                  {t("valid_until")}:{" "}
                  {dayjs(p.end_date).format("DD.MM.YYYY")}
                </span>
              </div>
            )}

            {hasFooter && <div className="h-4" />}
          </div>
        </div>

        {/* Sticky footer — only when link present */}
        {hasFooter && (
          <DrawerBody className="shrink-0 !p-4 !pb-safe-bottom !bg-gray-4 border-t border-white/08">
            <BaseButton
              text={t("open_link")}
              variant="primary"
              className="w-full"
              onClick={handleOpenLink}
              vibrate
            />
          </DrawerBody>
        )}
      </DrawerContent>
    </Drawer>
  );
}
